/**
 * View maths of `NxPlanCanvas`: which world point sits in the middle of the
 * viewport and how far it is zoomed. `zoom` is a factor on `unitPx` (screen
 * pixels per world unit at 100 %), so `pixelsPerUnit = zoom × unitPx`.
 * Screen coordinates are CSS pixels relative to the viewport's top left.
 */
import type { NxPlanBounds, NxPlanPoint } from './geometry';

export interface NxPlanView {
  /** World point in the middle of the viewport. */
  center: NxPlanPoint;
  /** Factor on `unitPx`; 1 = 100 %. */
  zoom: number;
}

export interface NxPlanSize {
  width: number;
  height: number;
}

export interface NxPlanZoomLimits {
  minZoom: number;
  maxZoom: number;
}

/** Everything the view maths needs besides the view: viewport, scale and limits. */
export interface NxPlanFrame extends NxPlanZoomLimits {
  size: NxPlanSize;
  /** Screen pixels per world unit at zoom 1. */
  unitPx: number;
}

export interface NxPlanTransform {
  /** Screen pixels per world unit. */
  pixelsPerUnit: number;
  worldToScreen: (point: NxPlanPoint) => NxPlanPoint;
  screenToWorld: (point: NxPlanPoint) => NxPlanPoint;
  /** World length of `px` screen pixels — zoom-independent strokes and handles. */
  px: (px: number) => number;
  /** World rectangle currently visible. */
  visibleBounds: NxPlanBounds;
  /** SVG `transform` that maps world units onto the viewport. */
  svgTransform: string;
}

export function clampZoom(zoom: number, limits: NxPlanZoomLimits): number {
  if (!Number.isFinite(zoom)) return Math.min(limits.maxZoom, Math.max(limits.minZoom, 1));
  return Math.min(limits.maxZoom, Math.max(limits.minZoom, zoom));
}

/** The transform helpers for a view in a viewport of `size`. */
export function planTransform(view: NxPlanView, size: NxPlanSize, unitPx: number): NxPlanTransform {
  const ppu = view.zoom * unitPx;
  const tx = size.width / 2 - view.center.x * ppu;
  const ty = size.height / 2 - view.center.y * ppu;
  const halfW = size.width / 2 / ppu;
  const halfH = size.height / 2 / ppu;
  return {
    pixelsPerUnit: ppu,
    worldToScreen: (p) => ({ x: p.x * ppu + tx, y: p.y * ppu + ty }),
    screenToWorld: (p) => ({ x: (p.x - tx) / ppu, y: (p.y - ty) / ppu }),
    px: (n) => n / ppu,
    visibleBounds: {
      minX: view.center.x - halfW,
      minY: view.center.y - halfH,
      maxX: view.center.x + halfW,
      maxY: view.center.y + halfH,
    },
    svgTransform: `matrix(${ppu} 0 0 ${ppu} ${tx} ${ty})`,
  };
}

/**
 * Zooms by `factor` around the screen point `anchor`: the world point under
 * the anchor stays under it (zoom to cursor). The result respects the limits;
 * a factor that hits a limit only zooms as far as allowed.
 */
export function zoomViewAt(
  view: NxPlanView,
  factor: number,
  anchor: NxPlanPoint,
  frame: NxPlanFrame
): NxPlanView {
  const { size, unitPx } = frame;
  const zoom = clampZoom(view.zoom * factor, frame);
  if (zoom === view.zoom) return view;
  const before = planTransform(view, size, unitPx).screenToWorld(anchor);
  const ppu = zoom * unitPx;
  // Solve for the centre that puts `before` back under `anchor`.
  return {
    zoom,
    center: {
      x: before.x - (anchor.x - size.width / 2) / ppu,
      y: before.y - (anchor.y - size.height / 2) / ppu,
    },
  };
}

/** Moves the content by `dx`/`dy` screen pixels (dragging it right shows what is left of it). */
export function panView(view: NxPlanView, dx: number, dy: number, unitPx: number): NxPlanView {
  const ppu = view.zoom * unitPx;
  return { zoom: view.zoom, center: { x: view.center.x - dx / ppu, y: view.center.y - dy / ppu } };
}

export interface NxPlanFitOptions {
  /** Screen pixels kept free around the bounds. */
  padding?: number;
  /** Zoom for zero-size bounds (a single point). Default 1. */
  fallbackZoom?: number;
}

/**
 * The view that shows `bounds` completely, centred, with `padding` screen
 * pixels around it. Zero-size bounds (a single point) keep `fallbackZoom`.
 */
export function fitView(
  bounds: NxPlanBounds,
  frame: NxPlanFrame,
  options: NxPlanFitOptions = {}
): NxPlanView {
  const { size, unitPx } = frame;
  const padding = options.padding ?? 0;
  const fallbackZoom = options.fallbackZoom ?? 1;
  const center = { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 };
  const bw = bounds.maxX - bounds.minX;
  const bh = bounds.maxY - bounds.minY;
  const availW = Math.max(1, size.width - 2 * padding);
  const availH = Math.max(1, size.height - 2 * padding);
  const candidates: number[] = [];
  if (bw > 0) candidates.push(availW / bw);
  if (bh > 0) candidates.push(availH / bh);
  const zoom = candidates.length ? Math.min(...candidates) / unitPx : fallbackZoom;
  return { center, zoom: clampZoom(zoom, frame) };
}

/** Largest „nice“ value (1, 2 or 5 × 10ⁿ) that is ≤ `value`. */
export function niceFloor(value: number): number {
  if (!(value > 0) || !Number.isFinite(value)) return 0;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const m = value / base;
  const mantissa = m >= 5 - 1e-9 ? 5 : m >= 2 - 1e-9 ? 2 : 1;
  return Number((mantissa * base).toPrecision(12));
}

/** Smallest „nice“ value (1, 2 or 5 × 10ⁿ) that is ≥ `value`. */
export function niceCeil(value: number): number {
  if (!(value > 0) || !Number.isFinite(value)) return 0;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const m = value / base;
  const mantissa = m <= 1 + 1e-9 ? 1 : m <= 2 + 1e-9 ? 2 : m <= 5 + 1e-9 ? 5 : 10;
  return Number((mantissa * base).toPrecision(12));
}

export interface NxPlanGridSteps {
  /** Minor line spacing in world units. */
  minor: number;
  /** Major line spacing in world units (a multiple of `minor`). */
  major: number;
}

/**
 * Grid spacing for the current scale: the minor step is the smallest nice
 * value (1/2/5 × 10ⁿ world units) at least `minMinorPx` apart on screen; the
 * major step is 5 minor steps (from 1 and 2) or 2 (from 5), so majors always
 * land on round numbers (0.1 → 0.5, 0.2 → 1, 0.5 → 1).
 */
export function planGridSteps(pixelsPerUnit: number, minMinorPx = 12): NxPlanGridSteps {
  const minor = niceCeil(minMinorPx / pixelsPerUnit);
  const mantissa = Math.round(minor / 10 ** Math.floor(Math.log10(minor) + 1e-9));
  const major = Number((minor * (mantissa === 5 ? 2 : 5)).toPrecision(12));
  return { minor, major };
}

export interface NxPlanScaleBar {
  /** Length in world units (a nice value). */
  length: number;
  /** Its width on screen in pixels. */
  px: number;
}

/** The scale-bar length: the largest nice world length no wider than `maxPx`. */
export function planScaleBar(pixelsPerUnit: number, maxPx = 120): NxPlanScaleBar {
  const length = niceFloor(maxPx / pixelsPerUnit);
  return { length, px: length * pixelsPerUnit };
}
